import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import strings from "../global/strings";
import { omitEmpty } from "./query-utils";

const LIMIT = 10;

export const employeesQueryKey = ["employees"] as const;

// A logins-table row (billing_system_BE/models/login.js).
export interface LoginRecord {
	login_info_id: string;
	username: string;
}

// The company_user join row (billing_system_BE/models/companyUser.js) — nested
// under Employee.company_user by getAllUser/searchEmployee/addLogin's include.
// Singular and nullable, not an array: these endpoints are always scoped to
// one company_info_id, so there's at most one row per user.
export interface CompanyUserRole {
	company_user_id?: string;
	user_info_id?: string;
	company_info_id?: string;
	user_role: "owner" | "user";
	company_user_status?: "active" | "inactive";
	// Set only when this company has split off its own Login for this user
	// (billing_system_BE/models/companyUser.js login_info_id FK).
	login_info_id?: string | null;
	company_login?: LoginRecord | null;
}

// Mirrors the users table (billing_system_BE/models/user.js), password always
// excluded server-side. This is the row shape returned by user, user/search,
// and user/update.
export interface Employee {
	user_info_id: string;
	user_id?: number;
	first_name: string;
	last_name: string;
	user_gender?: string | null;
	user_email: string;
	user_mobile: string;
	user_address?: string | null;
	user_city?: string | null;
	user_country?: string | null;
	user_image?: string | null;
	user_verified?: boolean;
	// null only appears transiently in employee-profile.component.tsx's form
	// state before real data (or a delete-before-send for new employees)
	// overwrites it — the backend column itself is never-null.
	user_status?: string | null;
	company_user?: CompanyUserRole | null;
	// All logins this user has across every company (getAllUser/searchEmployee
	// include Login separately, ordered by createdAt — see user.controller.js).
	logins?: LoginRecord[];
	createdAt?: string;
	updatedAt?: string;
	// FE-only flag, set client-side after a successful add (see employee-profile.component.tsx)
	isNew?: boolean;
}

// The employee branch of roleAccess() in src/global/function.js — returned
// per current user_role (owner/user), used to gate edit/delete affordances.
export interface EmployeePermissions {
	edit: boolean;
	view: boolean;
	delete: boolean;
}

export interface EmployeeFilters {
	user_id?: string;
	first_name?: string;
	last_name?: string;
	user_mobile?: string;
	user_email?: string;
	// Maps to searchEmployee's $logins.username$ filter (user.controller.js)
	username?: string;
}

interface EmployeesPageResult {
	rows: Employee[];
	count: number;
}

// Mirrors the old fetchCompanyEmployees/searchEmployees thunks (removed along
// with employeeReducer once this migration landed): no filters -> user,
// any filter present -> user/search. Filters are stripped of empty values
// here (not just at the caller) so a filters object whose keys are all ""
// still correctly counts as no filters applied.
async function fetchEmployeesPage(
	filters: EmployeeFilters,
	page: number,
): Promise<EmployeesPageResult> {
	const cleanFilters = omitEmpty(filters);
	const hasFilters = Object.keys(cleanFilters).length > 0;

	const url = `${strings.url}user${hasFilters ? "/search" : ""}`;
	const params = { ...cleanFilters, limit: LIMIT, page };

	const res = await axios.get(url, { params, withCredentials: true });

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { rows: res.data.result.rows, count: res.data.result.count };
}

// Infinite-scroll employee list.
export function useEmployees(filters: EmployeeFilters) {
	return useInfiniteQuery({
		queryKey: [...employeesQueryKey, filters],
		queryFn: ({ pageParam }) => fetchEmployeesPage(filters, pageParam),
		initialPageParam: 1,
		getNextPageParam: (lastPage, allPages) => {
			const fetchedCount = allPages.reduce((sum, p) => sum + p.rows.length, 0);
			return fetchedCount < lastPage.count ? allPages.length + 1 : undefined;
		},
	});
}

// addLogin's request body (billing_system_BE/controllers/user.controller.js)
// destructures username/password/user_role/user_info_id off the top level,
// plus the rest of Employee's own fields — not a distinct shape from what the
// form collects. Two mutually exclusive shapes share this one type:
// Path A (link existing) sends only { user_info_id, user_role }; Path B
// (create new) sends the full Employee fields + username + password, no
// user_info_id. Also reused as employee-profile.component.tsx's (edit-mode)
// formik values type: the form merges the full `employee` prop into its
// values at runtime, so it needs to hold every Employee field too, not just
// the ones it initializes explicitly.
export interface EmployeePayload extends Partial<Employee> {
	password?: string;
	user_role?: "owner" | "user";
	// Path B only — the new Login's username. Not an Employee field itself
	// (that lives on the separate logins table), so not covered by Partial<Employee>.
	username?: string;
	// updateUser-only (billing_system_BE/controllers/user.controller.js):
	// split_login creates a company-specific Login (requires username+password
	// and only takes effect when this company doesn't already have one);
	// merge_login removes this company's split Login and falls back to the
	// shared one. Mutually exclusive — never send both.
	split_login?: boolean;
	merge_login?: boolean;
}

// updateUser's personal-fields update (billing_system_BE/controllers/
// user.controller.js) has no undefined-filtering — it runs unconditionally on
// every call that reaches it, regardless of which fields the request actually
// intends to change (login-only, role-only, etc.), and writes exactly the
// keys present in the payload. Omitting one nulls it out server-side. Any
// caller that isn't editing the personal-fields form itself (EmployeeLoginManager,
// EmployeeRoleManager) must round-trip this snapshot alongside whatever it's
// actually changing.
export function buildProfileSnapshotPayload(user: Employee): EmployeePayload {
	return {
		user_info_id: user.user_info_id,
		first_name: user.first_name,
		last_name: user.last_name,
		user_gender: user.user_gender,
		user_email: user.user_email,
		user_mobile: user.user_mobile,
		user_address: user.user_address,
		user_city: user.user_city,
		user_country: user.user_country,
		user_status: user.user_status,
	};
}

interface AddEmployeeResult {
	success: boolean;
	result?: Employee;
	message?: string;
}

async function postAddEmployee(employeeData: EmployeePayload): Promise<AddEmployeeResult> {
	const res = await axios.post(`${strings.url}user/add`, employeeData, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data;
}

export function useAddEmployee() {
	return useMutation({
		mutationFn: postAddEmployee,
	});
}

interface UpdateEmployeeVars {
	employeeData: EmployeePayload;
	page?: number;
}

// Thrown instead of a plain Error when updateUser's username-conflict branch
// fires (billing_system_BE/controllers/user.controller.js, "own_shared_login"/
// "own_split_login") — carries enough structure for the caller to offer a
// merge action inline instead of just showing the message as a dead-end error.
export class UsernameConflictError extends Error {
	conflict_type: UsernameConflictType;
	merge_available: boolean;
	shared_login?: LoginRecord;

	constructor(
		message: string,
		conflict_type: UsernameConflictType,
		merge_available: boolean,
		shared_login?: LoginRecord,
	) {
		super(message);
		this.name = "UsernameConflictError";
		this.conflict_type = conflict_type;
		this.merge_available = merge_available;
		this.shared_login = shared_login;
	}
}

async function putUpdateEmployee({
	employeeData,
	page = 1,
}: UpdateEmployeeVars): Promise<{ rows: Employee[] }> {
	const res = await axios.put(`${strings.url}user/update`, employeeData, {
		params: { limit: LIMIT, page },
		withCredentials: true,
	});

	if (!res.data.success) {
		if (res.data.conflict_type) {
			throw new UsernameConflictError(
				res.data.message,
				res.data.conflict_type,
				Boolean(res.data.merge_available),
				res.data.shared_login,
			);
		}
		throw new Error(res.data.message);
	}

	return { rows: res.data.result.rows };
}

export function useUpdateEmployee() {
	return useMutation({
		mutationFn: putUpdateEmployee,
	});
}

// Thrown instead of a plain Error when deleteUser rejects because the
// employee has activity history (billing_system_BE/controllers/
// user.controller.js) — deleteUser is hard-delete-or-reject only now (no more
// silent soft-deactivate fallback); deactivating lives at useUpdateUserStatus
// instead. Callers can catch this specifically to point at that action rather
// than showing a dead-end error.
export class EmployeeHasActivityError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "EmployeeHasActivityError";
	}
}

async function deleteEmployeeById(userInfoId: string): Promise<{ rows: Employee[] }> {
	const res = await axios.delete(`${strings.url}user/delete`, {
		params: { user_info_id: userInfoId },
		headers: { "content-type": "application/json" },
		withCredentials: true,
	});

	if (!res.data.success) {
		if (res.data.error_type === "has_activity") {
			throw new EmployeeHasActivityError(res.data.message);
		}
		throw new Error(res.data.message);
	}

	return { rows: res.data.result.rows };
}

export function useDeleteEmployee() {
	return useMutation({
		mutationFn: deleteEmployeeById,
	});
}

// company_user_status is per-company (CompanyUsers row) — distinct from the
// global Employee.user_status the backend now only ever sets as a side
// effect (only flips it when this is the user's only company). Never send
// user_status directly; PUT /user/status is the only entry point for this.
export type CompanyUserStatus = "active" | "inactive";

interface UpdateUserStatusVars {
	user_info_id: string;
	company_user_status: CompanyUserStatus;
}

async function putUserStatus({
	user_info_id,
	company_user_status,
}: UpdateUserStatusVars): Promise<void> {
	const res = await axios.put(
		`${strings.url}user/status`,
		{ user_info_id, company_user_status },
		{ withCredentials: true },
	);

	if (!res.data.success) {
		throw new Error(res.data.message);
	}
}

export function useUpdateUserStatus() {
	return useMutation({
		mutationFn: putUserStatus,
	});
}

interface EmployeeBill {
	invoice_number?: string;
	invoice_date?: string;
	sub_total?: number;
	bill_void?: boolean;
}

// user/id's response is NOT an Employee row — it's a CompanyUsers.findOne()
// result (billing_system_BE/controllers/user.controller.js getUserById), with
// the User nested under `user` and Bills under `bills`. Confirmed by reading
// the controller directly rather than assumed from the customer/id shape
// (which IS a flat row — the two endpoints don't mirror each other here).
export interface EmployeeDetails {
	company_user_id?: string;
	user_role?: "owner" | "user";
	company_user_status?: "active" | "inactive";
	user?: Employee;
	bills?: EmployeeBill[];
	// Sibling fields on the user/id response (billing_system_BE/controllers/
	// user.controller.js getUserById), NOT nested inside `result` — see below.
	// true = this company has no split login; editing username/password here
	// affects every other company that shares this login.
	login_shared: boolean;
	// how many other companies share this login (only meaningful when login_shared)
	other_company_count: number;
	// true = this company already has its own separate Login for this user
	is_login_split: boolean;
	// the shared login this company would fall back to on a merge, if any
	shared_login: LoginRecord | null;
	// this company's own split login (CompanyUsers.login_info_id's Login), if
	// is_login_split — getUserById includes it as a sibling on the same row
	// (as: "company_login"), untyped here until now since only the derived
	// login_shared/is_login_split flags were previously read off this response.
	company_login?: LoginRecord | null;
}

async function fetchEmployeeDetails(
	userInfoId: string,
	limit: number,
): Promise<EmployeeDetails> {
	const res = await axios.get(`${strings.url}user/id`, {
		params: { user_info_id: userInfoId, limit },
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	// login_shared/other_company_count/is_login_split/shared_login are returned
	// as siblings of `result`, not nested inside it.
	return {
		...res.data.result,
		login_shared: res.data.login_shared,
		other_company_count: res.data.other_company_count,
		is_login_split: res.data.is_login_split,
		shared_login: res.data.shared_login,
	};
}

// Self-contained to the employee-profile side-modal (bill preview list) — grep-
// confirmed no other page/component reads this data.
export function useEmployeeDetails(userInfoId?: string, limit = 5) {
	return useQuery({
		queryKey: ["employeeDetails", userInfoId, limit],
		queryFn: () => fetchEmployeeDetails(userInfoId as string, limit),
		enabled: Boolean(userInfoId),
	});
}

// user/find's response row (billing_system_BE/controllers/user.controller.js
// findUsers) — a narrower, flatter shape than Employee/EmployeesPageResult:
// no company_user, and logins only carries `username` (no login_info_id).
export interface FoundUser {
	user_info_id: string;
	user_id?: number;
	first_name: string;
	last_name: string;
	user_email: string;
	user_mobile: string;
	user_image?: string | null;
	logins?: { username: string }[];
	// true if a CompanyUsers row already exists for this company (active OR
	// inactive) — findUsers doesn't filter by status, so this only means
	// "don't call addLogin," not "definitely currently active." The add-
	// employee-wizard redirects straight to the profile either way and lets
	// the profile's own status affordance handle reactivating an inactive one.
	in_company: boolean;
}

async function fetchFindUsers(q: string): Promise<FoundUser[]> {
	const res = await axios.get(`${strings.url}user/find`, {
		params: { q },
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

// Global cross-company user search for the add-user wizard's "search & link"
// step. BE itself gates on q.length < 2 and caps at 10 results, mirrored here
// only via `enabled` so we don't fire a request BE will just no-op on.
export function useFindUsers(q: string) {
	const trimmed = q.trim();
	return useQuery({
		queryKey: ["users", "find", trimmed],
		queryFn: () => fetchFindUsers(trimmed),
		enabled: trimmed.length >= 2,
		staleTime: 30 * 1000,
	});
}

export type UsernameConflictType =
	| "different_user"
	| "own_shared_login"
	| "own_split_login";

// user/check-username's response has no `success`/`result` envelope — `available`
// IS the payload. Do not reuse the generic `!res.data.success` throw pattern here.
export interface CheckUsernameResult {
	available: boolean | null;
	conflict_type?: UsernameConflictType;
	merge_available?: boolean;
	shared_login?: LoginRecord;
	message?: string;
}

async function fetchCheckUsername(
	username: string,
	userInfoId?: string,
): Promise<CheckUsernameResult> {
	const res = await axios.get(`${strings.url}user/check-username`, {
		params: {
			username,
			...(userInfoId ? { user_info_id: userInfoId } : {}),
		},
		withCredentials: true,
	});

	return res.data;
}

// Exact/authoritative username-availability check, distinct from useFindUsers'
// fuzzy discovery. `userInfoId` lets an edit-mode caller check a username
// against "is this still mine" rather than a flat taken/not-taken result.
export function useCheckUsername(username: string, userInfoId?: string) {
	const trimmed = username.trim().toLowerCase();
	return useQuery({
		queryKey: ["users", "check-username", trimmed, userInfoId],
		queryFn: () => fetchCheckUsername(trimmed, userInfoId),
		enabled: trimmed.length >= 3,
		staleTime: 10 * 1000,
	});
}
